import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { Event } from '../../../../../../models/event';
import { ScheduledBooking } from '../../../../../../models/scheduled-booking';
import { EventService } from '../../../../../../services/event/event.service';
import { AccountService } from '../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { ScheduledBookingService } from '../../../../../../services/scheduled-booking/scheduled-booking.service';
import { dateStringFromISO8601, dayOfWeekStringFromISO8601, timeStringFromISO8601 } from '../../../../../../utils';
import { MyAccount } from '../../../../../../models/my-account';
import { ScheduleType } from '../../../../../../models/event-type-enums';

enum refundOptions {
  noRefund,
  partialRefund,
  fullRefund
}

@Component({
  selector: 'app-cancel-event-modal',
  templateUrl: './cancel-event-modal.component.html',
  styleUrls: ['./cancel-event-modal.component.css']
})
export class CancelEventModalComponent implements OnInit {

  @Input() eventId: number;
  @Input() savedCallback: () => undefined;

  // Set by the caller for private events. When the event is private, the modal
  // offers refund options (PRO-1229) and cancels via the private-event
  // endpoint. The booking ID lets the modal load the booking's payment info in
  // order to show those options.
  @Input() isPrivate = false;
  @Input() bookingId: number = null;

  public event: Event;
  public booking: ScheduledBooking = null;

  public sendCancellationEmails = true;
  public cancellationEmailText = '';

  // Refund state, only used for private events. Defaults to a full refund.
  public refundSelection: refundOptions = refundOptions.fullRefund;
  public refundCurrencyAmount = 0;
  public refundVoucherCount = 0;
  public refundOptions = refundOptions;

  public loadingEvent = false;
  public loadingBooking = false;
  public loadingCancellation = false;

  public eventCancellationErrorMessage = '';

  public eventNotFound = false;
  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public dayOfWeekStringFromISO8601 = dayOfWeekStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;
  public ScheduleType = ScheduleType;

  public myAccount: MyAccount;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    public organizationConfigService: OrganizationConfigService,
    private eventService: EventService,
    private bookingService: ScheduledBookingService
  ) {}

  ngOnInit() {
    this.myAccount = this.accountService.getMyAccount();

    this.loadEvent(this.eventId);

    if (this.isPrivate && this.bookingId) {
      this.loadBooking();
    }
  }

  public loadEvent(eventId: number) {
    this.loadingEvent = true;

    this.eventService
      .getEvent(eventId)
      .then(event => {
        this.event = event;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEvent = false; });
  }

  public loadBooking() {
    if (!this.bookingId) { return; }

    this.loadingBooking = true;

    this.bookingService
      .getScheduledBooking(this.bookingId)
      .then(booking => {
        this.booking = booking;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          // No booking to refund; the event can still be cancelled.
          this.booking = null;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingBooking = false; });
  }

  /**
   * Whether the private event's booking has an amount paid which has not yet
   * been refunded. Refund options are only shown in this case.
   */
  public hasRefundableAmount(): boolean {
    return (
      !!this.booking &&
      (
        (this.booking.unrefunded_currency_amount > 0) ||
        (this.booking.unrefunded_num_vouchers > 0)
      )
    );
  }

  public onCancelSubmit() {
    this.loadingCancellation = true;

    this.eventCancellationErrorMessage = '';

    if (this.isPrivate) {
      this.onCancelPrivateEventSubmit();
      return;
    }

    this.eventService
      .cancelEvent(
        this.event.id,
        this.sendCancellationEmails,
        this.cancellationEmailText
      )
      .then(() => {
        this.savedCallback();
        this.activeModal.close();
      })
      .catch(reason => { this.handleCancelError(reason); })
      .finally(() => { this.loadingCancellation = false; });
  }

  private onCancelPrivateEventSubmit() {
    let refundCurrencyAmount = 0;
    let refundVoucherCount = 0;

    if (this.hasRefundableAmount()) {
      if (this.refundSelection === refundOptions.partialRefund) {
        refundCurrencyAmount = this.refundCurrencyAmount;
        refundVoucherCount = this.refundVoucherCount;
      } else if (this.refundSelection === refundOptions.fullRefund) {
        refundCurrencyAmount = this.booking.unrefunded_currency_amount;
        refundVoucherCount = this.booking.unrefunded_num_vouchers;
      }
      // refundOptions.noRefund leaves both amounts at zero.
    }

    this.eventService
      .cancelPrivateEvent(
        this.event.id,
        refundCurrencyAmount,
        refundVoucherCount,
        this.sendCancellationEmails,
        this.cancellationEmailText
      )
      .then(() => {
        this.savedCallback();
        this.activeModal.close();
      })
      .catch(reason => { this.handleCancelError(reason); })
      .finally(() => { this.loadingCancellation = false; });
  }

  private handleCancelError(reason: any) {
    if (reason.status === 400) {
      this.eventCancellationErrorMessage = reason.error;

      // Reload the event because its num_reservations may have changed, and
      // the booking because its refunded amounts may have changed.
      this.loadEvent(this.eventId);
      if (this.isPrivate && this.bookingId) {
        this.loadBooking();
      }
    } else if (reason.status === 403) {
      this.eventCancellationErrorMessage = 'You do not have permission to cancel this event.';
    } else {
      this.serverError = reason.status;
    }
  }

  public isLoading() {
    return this.loadingEvent || this.loadingBooking || this.loadingCancellation;
  }

}
